import React, { useState, useEffect } from 'react';
import { BACKEND_URL } from '../../config';
import { useLanguage } from '../../contexts/LanguageContext';
import CashCounter from './CashCounter';
import { openCashDrawer } from '../../utils/receiptPrinter';

export default function RegisterClose({ theme, user, session, onSuccess, onCancel }) {
  const { t } = useLanguage();
  const [denominations, setDenominations] = useState({});
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [sessionData, setSessionData] = useState(null);

  // Fetch session details
  useEffect(() => {
    fetchSessionData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session.session_id]);

  const fetchSessionData = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/reports/register-summary/${session.session_id}`);
      if (response.ok) {
        const data = await response.json();
        setSessionData(data);
      }
    } catch (err) {
      console.error('Error fetching session data:', err);
    }
  };

  const calculateTotal = () => {
    const values = {
      '1': 1, '2': 2, '5': 5, '10': 10, '20': 20,
      '50': 50, '100': 100, '200': 200, '500': 500, '1000': 1000
    };

    return Object.entries(denominations).reduce((total, [denom, count]) => {
      return total + ((values[denom] || 0) * (parseInt(count) || 0));
    }, 0);
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${BACKEND_URL}/api/registers/close`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_id: session.session_id,
          user_id: user.id,
          denominations,
          notes
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to close register');
      }

      openCashDrawer().catch(() => {});
      onSuccess(data);
    } catch (err) {
      console.error('Error closing register:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const actualCash = calculateTotal();
  const expectedCash = parseFloat(session.expected_cash || 0);
  const difference = actualCash - expectedCash;

  return (
    <div style={{
      maxWidth: '1000px',
      margin: '0 auto',
      padding: '2rem'
    }}>
      <div style={{
        backgroundColor: theme?.colors?.surface || '#fff',
        borderRadius: '12px',
        padding: '2rem',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
      }}>
        <h2 style={{
          margin: '0 0 1.5rem 0',
          color: theme?.colors?.text || '#000',
          fontSize: '1.5rem',
          fontWeight: 600
        }}>
          {t('closeRegister')} - {session.register_name}
        </h2>

        {/* Session Summary */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '1rem',
          marginBottom: '2rem',
          padding: '1rem',
          backgroundColor: theme?.colors?.background || '#f5f5f5',
          borderRadius: '8px'
        }}>
          <div>
            <div style={{ fontSize: '0.85rem', color: theme?.colors?.textSecondary || '#666' }}>
              {t('openingCash')}
            </div>
            <div style={{ fontSize: '1.2rem', fontWeight: 600, color: theme?.colors?.text || '#000' }}>
              ${parseFloat(session.opening_cash || 0).toFixed(2)}
            </div>
          </div>
          <div>
            <div style={{ fontSize: '0.85rem', color: theme?.colors?.textSecondary || '#666' }}>
              {t('cashSales')}
            </div>
            <div style={{ fontSize: '1.2rem', fontWeight: 600, color: theme?.colors?.success || '#4caf50' }}>
              ${parseFloat(session.total_cash_sales || 0).toFixed(2)}
            </div>
          </div>
          <div>
            <div style={{ fontSize: '0.85rem', color: theme?.colors?.textSecondary || '#666' }}>
              {t('payouts')}
            </div>
            <div style={{ fontSize: '1.2rem', fontWeight: 600, color: theme?.colors?.error || '#f44336' }}>
              ${parseFloat(sessionData?.total_payouts_amount || session.total_payouts || 0).toFixed(2)}
            </div>
          </div>
          <div>
            <div style={{ fontSize: '0.85rem', color: theme?.colors?.textSecondary || '#666' }}>
              {t('deposits')}
            </div>
            <div style={{ fontSize: '1.2rem', fontWeight: 600, color: theme?.colors?.success || '#4caf50' }}>
              ${parseFloat(sessionData?.total_deposits_amount || session.total_deposits || 0).toFixed(2)}
            </div>
          </div>
          <div>
            <div style={{ fontSize: '0.85rem', color: theme?.colors?.textSecondary || '#666' }}>
              {t('expectedCash')}
            </div>
            <div style={{ fontSize: '1.2rem', fontWeight: 600, color: theme?.colors?.primary || '#1976d2' }}>
              ${expectedCash.toFixed(2)}
            </div>
          </div>
        </div>

        {/* Cash Counter */}
        <CashCounter
          theme={theme}
          denominations={denominations}
          onChange={setDenominations}
        />

        {/* Difference Display */}
        {actualCash > 0 && (
          <div style={{
            marginTop: '1.5rem',
            padding: '1.5rem',
            backgroundColor: theme?.colors?.surface || '#fff',
            border: `2px solid ${
              difference === 0
                ? theme?.colors?.success || '#4caf50'
                : difference > 0
                ? theme?.colors?.warning || '#ff9800'
                : theme?.colors?.error || '#f44336'
            }`,
            borderRadius: '8px'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <div>
                <div style={{
                  fontSize: '0.9rem',
                  marginBottom: '0.25rem',
                  color: theme?.colors?.text || '#000'
                }}>
                  {t('actualCash')}: ${actualCash.toFixed(2)}
                </div>
                <div style={{
                  fontSize: '0.9rem',
                  color: theme?.colors?.text || '#000'
                }}>
                  {t('expectedCash')}: ${expectedCash.toFixed(2)}
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{
                  fontSize: '0.9rem',
                  marginBottom: '0.25rem',
                  color: theme?.colors?.text || '#000'
                }}>
                  {difference === 0 ? t('exact') : difference > 0 ? t('over') : t('short')}
                </div>
                <div style={{
                  fontSize: '1.8rem',
                  fontWeight: 700,
                  color: difference === 0
                    ? theme?.colors?.success || '#4caf50'
                    : difference > 0
                    ? theme?.colors?.warning || '#ff9800'
                    : theme?.colors?.error || '#f44336'
                }}>
                  {difference >= 0 ? '+' : ''}{difference.toFixed(2)}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Notes */}
        <div style={{ marginTop: '1.5rem' }}>
          <label style={{
            display: 'block',
            marginBottom: '0.5rem',
            fontWeight: 500,
            color: theme?.colors?.text || '#000'
          }}>
            {t('notes')} ({t('optional')}):
          </label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            placeholder={t('addNotesAboutShift')}
            style={{
              width: '100%',
              padding: '0.75rem',
              fontSize: '1rem',
              border: `1px solid ${theme?.colors?.border || '#ddd'}`,
              borderRadius: '6px',
              backgroundColor: theme?.colors?.surface || '#fff',
              color: theme?.colors?.text || '#000',
              resize: 'vertical'
            }}
          />
        </div>

        {/* Error Message */}
        {error && (
          <div style={{
            marginTop: '1rem',
            padding: '1rem',
            backgroundColor: theme?.colors?.surface || '#fff',
            border: `1px solid ${theme?.colors?.error || '#f00'}`,
            borderRadius: '6px',
            color: theme?.colors?.error || '#f00'
          }}>
            {error}
          </div>
        )}

        {/* Action Buttons */}
        <div style={{
          marginTop: '2rem',
          display: 'flex',
          gap: '1rem',
          justifyContent: 'flex-end'
        }}>
          <button
            onClick={onCancel}
            disabled={loading}
            style={{
              padding: '0.75rem 1.5rem',
              fontSize: '1rem',
              fontWeight: 500,
              border: `1px solid ${theme?.colors?.border || '#ddd'}`,
              borderRadius: '6px',
              backgroundColor: theme?.colors?.surface || '#fff',
              color: theme?.colors?.text || '#000',
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.6 : 1
            }}
          >
            {t('cancel')}
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading || actualCash === 0}
            style={{
              padding: '0.75rem 2rem',
              fontSize: '1rem',
              fontWeight: 500,
              border: 'none',
              borderRadius: '6px',
              backgroundColor: theme?.colors?.primary || '#1976d2',
              color: theme?.colors?.buttonText || theme?.colors?.surface || '#fff',
              cursor: (loading || actualCash === 0) ? 'not-allowed' : 'pointer',
              opacity: (loading || actualCash === 0) ? 0.6 : 1
            }}
          >
            {loading ? t('closing') + '...' : t('closeRegister')}
          </button>
        </div>
      </div>
    </div>
  );
}
